import React, { useState, useEffect } from "react";
import globals from '../../../Globals';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Image as ImageIcon, Loader } from 'lucide-react';

const API = globals.API;

// Resize + compress an image in the browser before upload.
// Hero images are full-width backgrounds, so ~1920px wide JPEG is plenty and
// keeps the file small — this avoids the server "413 Request Entity Too Large".
const compressImage = (file, maxWidth = 1920, quality = 0.82) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, maxWidth / img.width);
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; // flatten any transparency (JPEG has none)
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error('Compression échouée')); return; }
      const name = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
      resolve(new File([blob], name, { type: 'image/jpeg' }));
    }, 'image/jpeg', quality);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image invalide')); };
  img.src = url;
});

const uploadSingleImage = async (file) => {
  // Compress first; fall back to the original if compression fails
  let toSend = file;
  try { toSend = await compressImage(file); } catch { toSend = file; }
  const fd = new FormData();
  fd.append('image', toSend);
  const res = await fetch(`${API}/events/upload-image`, { method: 'POST', body: fd });
  if (res.status === 413) throw new Error("Image trop lourde pour le serveur, réessayez avec une image plus petite.");
  const data = await res.json();
  if (!data.success) throw new Error(data.message || "Échec de l'upload");
  return { url: data.url, publicId: data.publicId };
};

const DashboardHero = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/settings`);
      const data = await res.json();
      setImages(data?.heroImages || []);
    } catch { notify("Impossible de charger les images.", 'error'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (!f.type.startsWith('image/')) continue;
        uploaded.push(await uploadSingleImage(f));
      }
      setImages(prev => [...prev, ...uploaded]);
      notify(`${uploaded.length} image(s) ajoutée(s). N'oubliez pas d'enregistrer.`);
    } catch (e) { notify(e.message || "Échec de l'upload.", 'error'); }
    setUploading(false);
  };

  const removeAt = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    setImages(prev => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/settings/hero`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ heroImages: images.map(i => ({ url: i.url, publicId: i.publicId || '' })) }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Échec');
      setImages(data.heroImages || []);
      notify("Images du carousel enregistrées ✓");
    } catch (e) { notify(e.message || "Échec de l'enregistrement.", 'error'); }
    setSaving(false);
  };

  const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
  const btn = { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '14px' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Images d'accueil (carousel)</h2>
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0' }}>Ces images défilent en haut de la page d'accueil. Glissez l'ordre avec les flèches.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label style={{ ...btn, background: '#eef2ff', color: '#4338ca', opacity: uploading ? 0.6 : 1 }}>
            {uploading ? <Loader size={16} className="spin" /> : <Plus size={16} />}
            {uploading ? 'Upload…' : 'Ajouter des images'}
            <input type="file" accept="image/*" multiple hidden disabled={uploading}
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
          </label>
          <button onClick={save} disabled={saving || loading} style={{ ...btn, background: '#01225C', color: 'white', opacity: (saving || loading) ? 0.6 : 1 }}>
            {saving ? <Loader size={16} className="spin" /> : <Save size={16} />}
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {toast && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 500,
          background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4', color: toast.type === 'error' ? '#991b1b' : '#166534',
          border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}` }}>
          {toast.msg}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>Chargement…</div>
      ) : images.length === 0 ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
          <ImageIcon size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p style={{ margin: 0 }}>Aucune image. La page d'accueil utilisera les images par défaut.</p>
          <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Cliquez sur « Ajouter des images » pour commencer.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '18px' }}>
          {images.map((img, idx) => (
            <div key={img.url + idx} style={card}>
              <div style={{ position: 'relative', height: '160px', background: '#f1f5f9' }}>
                <img src={img.url} alt={`hero ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <span style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(1,34,92,0.9)', color: 'white', fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px' }}>#{idx + 1}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button title="Monter" onClick={() => move(idx, -1)} disabled={idx === 0}
                    style={{ ...btn, padding: '7px', background: '#f1f5f9', color: '#475569', opacity: idx === 0 ? 0.4 : 1 }}><ArrowUp size={16} /></button>
                  <button title="Descendre" onClick={() => move(idx, 1)} disabled={idx === images.length - 1}
                    style={{ ...btn, padding: '7px', background: '#f1f5f9', color: '#475569', opacity: idx === images.length - 1 ? 0.4 : 1 }}><ArrowDown size={16} /></button>
                </div>
                <button title="Supprimer" onClick={() => removeAt(idx)}
                  style={{ ...btn, padding: '7px 12px', background: '#fef2f2', color: '#ef4444' }}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DashboardHero;
